/* istanbul ignore file */

import { Badge, Box, Flex, Small, Toggle } from "orcs-design-system";
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import styled, { useTheme, css } from "styled-components";
import { useMutation } from "@apollo/client/react";
import { get } from "lodash";
import {
  isInternalSupportUser,
  isProdAuth0Tenant,
  auth0UserMetadataKey,
} from "src/services/auth";
import SelectTenantPrompt from "src/components/SelectTenantPrompt";
import { saveActiveTenant } from "src/services/localStorage";
import eventEmitter, { EVENTS } from "src/services/eventEmitter";
import { useWorkspace } from "src/contexts/global/WorkspaceContext";
import { useMaintenanceMode } from "src/contexts/global/GlobalContext";
import setSupportImpersonationMutation from "src/queries/mutations/setSupportImpersonation.graphql";
import { getItem, saveItem } from "src/services/sessionStorage";

import ProdConfirmationDialog from "./ProdConfirmationDialog";
import SupportImpersonationControls from "./SupportImpersonationControls";

const ENV_INFORMATION_BAR_SESSION_KEY = "envInformationBar";

const Wrapper = styled(Box)`
  position: relative;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  z-index: 22;

  ${(p) =>
    p.$sliver &&
    css`
      appearance: none;
      -webkit-appearance: none;
      background-color: ${p.$sliverBg};
      flex-wrap: nowrap;
      min-height: 4px;
      height: 4px;
      padding: 0;
      width: 100%;
      border: none;
      border-radius: 0;
      cursor: pointer;
      font: inherit;
      text-align: left;
    `}
`;

const StateBadgeTrigger = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  color: inherit;
  font: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
`;

const geInitialMode = (overrideTenant) => {
  if (overrideTenant?.canRunMutation !== undefined) {
    return !overrideTenant?.canRunMutation;
  }

  return isProdAuth0Tenant();
};

const getEnvironmentBadge = (isProdTenant) => {
  if (isProdTenant) {
    return { label: "Production", variant: "danger" };
  }

  return { label: "Development", variant: "success" };
};

const EnvInformationBar = (props) => {
  const theme = useTheme();
  const workspace = useWorkspace();
  const maintenanceMode = useMaintenanceMode();
  const [setSupportImpersonation] = useMutation(
    setSupportImpersonationMutation
  );

  const { user, overrideTenant } = props;
  const userSub = user?.sub || "";

  const hasEmailImpersonation = Boolean(
    get(user, [auth0UserMetadataKey, "impersonation", "email"])
  );
  const isSupportUser = isInternalSupportUser(user) || hasEmailImpersonation;
  const isMaintenanceMode = maintenanceMode === "true" && isSupportUser;
  const [isReadonlyMode, setIsReadonlyMode] = useState(
    () => hasEmailImpersonation || geInitialMode(overrideTenant)
  );
  const [isModalVisible, setModalVisible] = useState(false);
  const [isSliver, setIsSliver] = useState(
    () => getItem(ENV_INFORMATION_BAR_SESSION_KEY)?.sliver === true
  );

  const effectiveReadonly = isReadonlyMode || hasEmailImpersonation;

  const customer = workspace?.config?.customer;
  const workspaceId = workspace?.id;

  useEffect(() => {
    if (
      !hasEmailImpersonation &&
      !isProdAuth0Tenant() &&
      overrideTenant &&
      overrideTenant.canRunMutation === undefined
    ) {
      overrideTenant.canRunMutation = true;
    }
    // Only need to do this for test env, so no need to listen to overrideTenant change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onConfirm = () => {
    if (hasEmailImpersonation) {
      setModalVisible(false);
      return;
    }
    const nextMode = !isReadonlyMode;
    setIsReadonlyMode(nextMode);

    // Add attribute to overrideTenant to enable mutating
    if (overrideTenant) {
      overrideTenant.canRunMutation = !nextMode;

      eventEmitter.emit(EVENTS.ACTIVE_TENANT_UPDATED, overrideTenant);
      saveActiveTenant(userSub, overrideTenant);
    }

    setModalVisible(false);
  };

  const onReadonlyModeToggle = () => {
    if (isProdAuth0Tenant() && isReadonlyMode) {
      setModalVisible(true);
    } else {
      onConfirm();
    }
  };

  const onClose = () => {
    setModalVisible(false);
  };

  let envBarColour;
  if (isMaintenanceMode) {
    envBarColour = theme?.colors?.warning80;
  } else if (effectiveReadonly) {
    envBarColour = theme?.colors?.warning80;
  } else if (isProdAuth0Tenant()) {
    envBarColour = theme?.colors?.danger80;
  } else {
    envBarColour = theme?.colors?.success80;
  }
  const environmentBadge = getEnvironmentBadge(isProdAuth0Tenant());
  const additionalBadgeLabels = [
    hasEmailImpersonation && "Email Impersonation",
    isMaintenanceMode && "Maintenance",
  ].filter(Boolean);

  const setSliverMode = (sliver) => {
    setIsSliver(sliver);
    saveItem(ENV_INFORMATION_BAR_SESSION_KEY, { sliver });
  };

  if (isSliver) {
    return (
      <Wrapper
        as="button"
        type="button"
        $sliver
        $sliverBg={envBarColour}
        bg={envBarColour}
        color="white"
        id="appEnvBar"
        aria-label="Expand env bar"
        aria-expanded={false}
        onClick={() => setSliverMode(false)}
      />
    );
  }

  return (
    <Wrapper bg={envBarColour} color="white" px="r" py="xxs" id="appEnvBar">
      <Flex
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        flexWrap="wrap"
      >
        <Flex flexWrap="wrap" alignItems="center">
          <Box mr="s">
            <StateBadgeTrigger
              type="button"
              aria-expanded={true}
              onClick={() => setSliverMode(true)}
            >
              <Badge noWrap={true} variant={environmentBadge.variant}>
                {environmentBadge.label}
              </Badge>
            </StateBadgeTrigger>
          </Box>
          {additionalBadgeLabels.map((label) => (
            <Badge key={label} noWrap={true} altStyle={true} ml="s">
              {label}
            </Badge>
          ))}
          <Small ml="s">{`code: ${customer ?? ""}`}</Small>
          <Small ml="s">{` | tenant: ${overrideTenant?.tenantId ?? ""}`}</Small>
          <Small ml="s">{` | workspace: ${workspaceId ?? ""}`}</Small>
          <Small ml="s">{` | ${overrideTenant?.label ?? ""}`}</Small>
        </Flex>
        <Flex alignItems="center" flexWrap="wrap">
          <SelectTenantPrompt {...props} isSelectOnly />
          <SupportImpersonationControls
            user={user}
            setSupportImpersonation={setSupportImpersonation}
          />
          <Box ml="s" bg="surfaceRaised" p="xs" pr="s" borderRadius="14px">
            <Toggle
              id="env-info-change-readonly"
              label="Allow changes"
              small
              onChange={onReadonlyModeToggle}
              checked={!effectiveReadonly}
              disabled={hasEmailImpersonation}
            />
          </Box>
        </Flex>
      </Flex>
      {isProdAuth0Tenant() && (
        <ProdConfirmationDialog
          visible={isModalVisible}
          onConfirm={onConfirm}
          onClose={onClose}
        />
      )}
    </Wrapper>
  );
};

EnvInformationBar.propTypes = {
  user: PropTypes.object.isRequired,
  overrideTenant: PropTypes.object.isRequired,
};

export default EnvInformationBar;
